import * as React from "react";
import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Permission } from "@poji/shared";
import { api, ApiError } from "./api";
import { supabase } from "./supabase";

/** /v1/staff/me yanıtı (K-092) */
export interface StaffMe {
  userId: string;
  isSuperAdmin: boolean;
  mfaRequired: boolean;
  mfaSatisfied: boolean;
  permissions: Permission[];
  marketScopes: string[];
}

/**
 * Davet ya da "şifremi unuttum" bağlantısıyla gelen kişi önce şifre belirler. Bağlantı türü adres
 * çubuğundaki #…type=invite|recovery bilgisinden okunur (Supabase oturumu da oradan kurar). Sayfa
 * yenilense de unutulmasın diye sekme belleğine yazılır; okunamazsa yalnızca bu açılışta geçerli.
 */
const MUST_SET_PASSWORD = "poji.mustSetPassword";
let mustSetPasswordMemory =
  typeof window !== "undefined" && /type=(invite|recovery)/.test(window.location.hash);
function readMustSetPassword(): boolean {
  try {
    if (mustSetPasswordMemory) sessionStorage.setItem(MUST_SET_PASSWORD, "1");
    return mustSetPasswordMemory || sessionStorage.getItem(MUST_SET_PASSWORD) === "1";
  } catch {
    return mustSetPasswordMemory;
  }
}
function writeMustSetPassword(v: boolean) {
  mustSetPasswordMemory = v;
  try {
    if (v) sessionStorage.setItem(MUST_SET_PASSWORD, "1");
    else sessionStorage.removeItem(MUST_SET_PASSWORD);
  } catch {
    /* tarayıcı belleği kapalı: bellekteki değer yeter */
  }
}

export type AuthState =
  | { kind: "loading" }
  | { kind: "set_password"; email: string | null }
  | { kind: "signed_out" }
  | { kind: "not_staff"; email: string | null }
  | { kind: "needs_mfa"; email: string | null }
  | { kind: "ready"; email: string | null; staff: StaffMe }
  | { kind: "error"; error: Error };

interface AuthContext {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** davet/şifre sıfırlama: yeni şifreyi kaydet */
  setPassword: (password: string) => Promise<void>;
  /** "şifremi unuttum": hesap varsa sıfırlama e-postası (yoksa da aynı yanıt) */
  sendPasswordReset: (email: string) => Promise<void>;
  /** 2FA sonrası token yenilenir: yetkileri yeniden oku */
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const Ctx = React.createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = React.useState<Session | null | undefined>(undefined);
  const [mustSetPassword, setMustSetPassword] = React.useState(readMustSetPassword);

  React.useEffect(() => {
    const sb = supabase();
    void sb.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = sb.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") {
        writeMustSetPassword(true);
        setMustSetPassword(true);
      }
      setSession(s);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const staff = useQuery({
    queryKey: ["staff-me", session?.access_token],
    queryFn: () => api<StaffMe>("/v1/staff/me"),
    enabled: Boolean(session),
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
    staleTime: 60_000,
  });

  const email = session?.user.email ?? null;
  let state: AuthState;
  if (session === undefined) state = { kind: "loading" };
  else if (!session) state = { kind: "signed_out" };
  else if (mustSetPassword) state = { kind: "set_password", email };
  else if (staff.isPending) state = { kind: "loading" };
  else if (staff.error) {
    const e = staff.error;
    state =
      e instanceof ApiError && e.status === 403 && e.reason === "not_staff"
        ? { kind: "not_staff", email }
        : e instanceof ApiError && e.status === 401
          ? { kind: "signed_out" }
          : { kind: "error", error: e };
  } else if (!staff.data.mfaSatisfied) state = { kind: "needs_mfa", email };
  else state = { kind: "ready", email, staff: staff.data };

  const value = React.useMemo<AuthContext>(
    () => ({
      state,
      async signIn(e, password) {
        const { error } = await supabase().auth.signInWithPassword({ email: e, password });
        if (error) throw error;
      },
      async setPassword(password) {
        const { error } = await supabase().auth.updateUser({ password });
        if (error) throw error;
        writeMustSetPassword(false);
        setMustSetPassword(false);
        // bağlantıdaki token'lar adres çubuğunda kalmasın
        if (window.location.hash) history.replaceState(null, "", window.location.pathname);
      },
      async sendPasswordReset(e) {
        const { error } = await supabase().auth.resetPasswordForEmail(e, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
      },
      async signOut() {
        writeMustSetPassword(false);
        setMustSetPassword(false);
        await supabase().auth.signOut();
        qc.clear();
      },
      async refresh() {
        const { data } = await supabase().auth.refreshSession();
        setSession(data.session);
        await qc.invalidateQueries({ queryKey: ["staff-me"] });
      },
      can(permission) {
        if (state.kind !== "ready") return false;
        return state.staff.isSuperAdmin || state.staff.permissions.includes(permission);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.kind, state.kind === "ready" ? state.staff : null, qc, email],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContext {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useAuth: AuthProvider yok");
  return v;
}

/** Testler ve önizleme için: sabit durumla sağlayıcı */
export function StaticAuthProvider({
  staff,
  children,
}: {
  staff: StaffMe;
  children: React.ReactNode;
}) {
  const value: AuthContext = {
    state: { kind: "ready", email: "admin@po-ji.com", staff },
    signIn: async () => {},
    setPassword: async () => {},
    sendPasswordReset: async () => {},
    signOut: async () => {},
    refresh: async () => {},
    can: (p) => staff.isSuperAdmin || staff.permissions.includes(p),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
